import { createContext, useState , useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import Header from "./components/Header";
import Footer from "./components/Footer";
import DemoModal from "./components/DemoModal";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import { logPageView } from "./analytics";

const mainWrapperStyle = {
    minHeight: "80vh",
};

const getFontClass = (lang: string) => {
    switch (lang) {
        case "ar":
            return "font-tajawl";
        case "en":
            return "font-montserrat";
        default:
            return "";
    }
};

export const ModalContext = createContext<{ openModal: () => void }>({
    openModal: () => {},
});

function App() {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;
    const fontClass = getFontClass(currentLang);
    const location = useLocation();
    const [isModalOpen, setModalOpen] = useState(false);

    const openModal = () => setModalOpen(true);
    const closeModal = () => setModalOpen(false);

    const outletContext = {
        openModal,
    };
    useEffect(() => {
        logPageView();
    }, [location]);

    useEffect(() => {
        document.title = t("app.title");
    }, [t, currentLang]);
    
    // Check for stale cache on mount
    useEffect(() => {
        // Check if service worker cache is causing issues
        const checkCacheHealth = async () => {
            if ('caches' in window) {
                try {
                    const cacheNames = await caches.keys();
                    // If there are old caches with old patterns, clear them
                    const oldCaches = cacheNames.filter(name => 
                        name.startsWith('nawastech-v1') && !name.includes(Date.now().toString().substring(0, 8))
                    );
                    
                    if (oldCaches.length > 0) {
                        console.log('Clearing old caches:', oldCaches);
                        await Promise.all(oldCaches.map(cache => caches.delete(cache)));
                    }
                } catch (error) {
                    console.error('Error checking cache health:', error);
                }
            }
        };
        
        checkCacheHealth();
    }, []);

    return (
        <>
            <Helmet>
                <html lang={currentLang} dir={currentLang === "ar" ? "rtl" : "ltr"} />
            </Helmet>
            <div className="min-h-screen flex flex-col">
                <Header openModal={openModal} />
                <main 
                    className={`${fontClass} flex-grow`} 
                    style={mainWrapperStyle}
                    role="main"
                    id="main-content">
                    <Outlet context={outletContext} />
                </main>
                <Footer />
            </div>
            <DemoModal isOpen={isModalOpen} onClose={closeModal} />
            <ToastContainer
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={currentLang === "ar"}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
                className="custom-toast-container"
            />
        </>
    );
}

export default App;
